import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 37:931 — Button):
 *
 * Variants: Default, Secondary, Destructive, Outline, Ghost, Link
 * States:   Default, Hover, Focus, Loading, Disabled, Pressed
 * Sizes:    xs (h-6), sm (h-7), default (h-8), lg (h-9),
 *           icon-xs (24), icon-sm (28), icon (32), icon-lg (36)
 *
 * Specs:
 *   Default size: h-8 (32px), px-2.5 (10px), py-2 (8px), gap-1.5 (6px)
 *   rounded-md (8px), text-sm (14px), font-medium (500)
 *   Icon placeholder: 16x16 (default), 12px (xs), 14px (sm)
 *   Loading: Loader2 spinner replaces left icon area
 *   Pressed: `active` prop (outline variant)
 *   Kbd group: bg-background/20 badges inside button
 */

const intuiButtonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Dark-mode pages are lighter than `background`, so a button painting
        // its own would sit darker than the surface and read as pressed. The
        // dark hover is repeated because it ties with the transparent rule on
        // specificity, and a tie is settled by stylesheet order.
        outline:
          'border border-input bg-background dark:bg-transparent hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-6 px-2 text-xs [&_svg]:size-3',
        sm: 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
        default: 'h-8 px-2.5 text-sm [&_svg]:size-4',
        lg: 'h-9 px-3 text-sm [&_svg]:size-4',
        'icon-xs': 'size-6 [&_svg]:size-3',
        'icon-sm': 'size-7 [&_svg]:size-3.5',
        icon: 'size-8 [&_svg]:size-4',
        'icon-lg': 'size-9 [&_svg]:size-4',
      },
      // The "Pressed" state from the Figma state list: a button that stays
      // engaged, e.g. a filter trigger while filters are applied. Only the
      // outline variant defines it, in compoundVariants below.
      active: {
        true: '',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        active: true,
        // `foreground` is the ink colour of the current theme, so this reads in
        // both. `primary` is a fill colour and its dark value is as dark as the
        // surface, which is what made the emphasis disappear in dark mode.
        class: 'border-foreground text-foreground',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface IntuiButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof intuiButtonVariants> {
  /** Render as child element (polymorphic) */
  asChild?: boolean
  /** Show loading spinner and disable interaction */
  loading?: boolean
  /** Icon rendered before the label */
  leftIcon?: React.ReactNode
  /** Icon rendered after the label */
  rightIcon?: React.ReactNode
}

const IntuiButton = React.forwardRef<HTMLButtonElement, IntuiButtonProps>(
  (
    {
      className,
      variant,
      size,
      active,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = disabled || loading

    return (
      <Comp
        className={cn(
          intuiButtonVariants({ variant, size, active, className }),
          isDisabled && asChild && 'pointer-events-none opacity-50',
        )}
        ref={ref}
        disabled={!asChild ? isDisabled : undefined}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" />}
        {!loading && leftIcon}
        <Slottable>{children}</Slottable>
        {rightIcon}
      </Comp>
    )
  },
)
IntuiButton.displayName = 'IntuiButton'

/**
 * Figma (node 18672:224279 — ButtonGroup):
 *
 * Connected buttons with shared borders/separators.
 * Variants: Default (dark), Outline (bordered), Secondary (gray)
 * Orientations: Horizontal, Vertical
 *
 * Horizontal: first child rounded-l-lg, last rounded-r-lg, middle rounded-none
 * Vertical: first child rounded-t-lg, last rounded-b-lg, middle rounded-none
 * Separators: Default = bg-primary-foreground/20, Secondary = bg-input, Outline = shared borders
 */
export interface IntuiButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outline' | 'secondary'
  orientation?: 'horizontal' | 'vertical'
}

function IntuiButtonGroup({
  variant = 'outline',
  orientation = 'horizontal',
  className,
  children,
  ...props
}: IntuiButtonGroupProps) {
  const isHorizontal = orientation === 'horizontal'

  // Rounding: strip from middle, apply to first/last
  const roundingClasses = isHorizontal
    ? '[&>*]:rounded-none [&>*:first-child]:rounded-l-lg [&>*:last-child]:rounded-r-lg'
    : '[&>*]:rounded-none [&>*:first-child]:rounded-t-lg [&>*:last-child]:rounded-b-lg'

  // Separator between children via border tricks
  const separatorClasses =
    variant === 'outline'
      ? isHorizontal
        ? '[&>*:not(:first-child)]:border-l-0'
        : '[&>*:not(:first-child)]:border-t-0'
      : variant === 'secondary'
        ? isHorizontal
          ? '[&>*+*]:border-l [&>*+*]:border-input'
          : '[&>*+*]:border-t [&>*+*]:border-input'
        : isHorizontal
          ? '[&>*+*]:border-l [&>*+*]:border-primary-foreground/20'
          : '[&>*+*]:border-t [&>*+*]:border-primary-foreground/20'

  return (
    <div
      className={cn(
        'inline-flex',
        !isHorizontal && 'flex-col',
        roundingClasses,
        separatorClasses,
        className,
      )}
      role="group"
      {...props}
    >
      {children}
    </div>
  )
}
IntuiButtonGroup.displayName = 'IntuiButtonGroup'

export { IntuiButton, intuiButtonVariants, IntuiButtonGroup }
